// components/RepliesPage/dialogs/HelpModal.tsx
import {
  HelpCircle,
  Twitter,
  MessageCircle,
  ShieldAlert,
  Check,
  X,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[89dvh] max-w-7xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            How Twitter Replies Work
          </DialogTitle>
          <DialogDescription>
            A guide to managing Twitter replies with your AI agent
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-8 py-4">
          {/* Main workflow diagram */}
          <div className="relative overflow-hidden p-6 bg-muted/10 border rounded-lg">
            <h3 className="text-lg font-medium mb-4">Twitter Reply Workflow</h3>
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              {/* Workflow step 1 */}
              <div className="bg-card p-3 rounded-lg border w-full md:w-60 flex flex-col items-center">
                <Twitter className="h-6 w-6 mb-2 text-blue-500" />
                <h4 className="font-medium text-center">Mentions & Tweets</h4>
                <p className="text-sm text-center text-muted-foreground mt-1">
                  Users mention your account or your agent proactively engages
                </p>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center">
                <ArrowRight className="hidden md:block h-6 w-6 text-muted-foreground" />
                <ArrowRight className="block md:hidden h-6 w-6 text-muted-foreground rotate-90" />
              </div>

              {/* Workflow step 2 */}
              <div className="bg-card p-3 rounded-lg border w-full md:w-60 flex flex-col items-center">
                <ShieldAlert className="h-6 w-6 mb-2 text-amber-500" />
                <h4 className="font-medium text-center">
                  Moderation & Filtering
                </h4>
                <p className="text-sm text-center text-muted-foreground mt-1">
                  Reply rules determine which tweets to respond to
                </p>
              </div>

              {/* Arrow */}
              <div className="flex items-center justify-center">
                <ArrowRight className="hidden md:block h-6 w-6 text-muted-foreground" />
                <ArrowRight className="block md:hidden h-6 w-6 text-muted-foreground rotate-90" />
              </div>

              {/* Workflow step 3 - Conditional fork */}
              <div className="w-full md:w-60 space-y-2">
                <div className="bg-card p-3 rounded-lg border flex flex-col items-center">
                  <div className="flex gap-1">
                    <Check className="h-5 w-5 text-green-500" />
                    <X className="h-5 w-5 text-red-500" />
                  </div>
                  <h4 className="font-medium text-center">Approval Process</h4>
                  <p className="text-sm text-center text-muted-foreground mt-1">
                    Auto-post or manual approval
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Automated Mode section */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-2 text-lg flex items-center gap-2">
              <div className="bg-green-500/20 text-green-500 rounded-full p-1.5">
                <Twitter className="h-4 w-4" />
              </div>
              Automated Mode
            </h4>
            <p className="text-muted-foreground">
              In automated mode, your AI agent will immediately generate and
              post replies to Twitter without requiring your manual approval.
              This is ideal for high-volume accounts that need real-time
              engagement or when you've fine-tuned your agent's responses to
              consistently match your expectations.
            </p>
            <div className="mt-3 bg-muted p-3 rounded-md">
              <p className="text-sm font-medium">Key Benefits:</p>
              <ul className="list-disc pl-5 text-sm text-muted-foreground mt-1 space-y-1">
                <li>Immediate response to followers</li>
                <li>Handles high volumes of mentions</li>
                <li>Works 24/7 without your intervention</li>
              </ul>
            </div>
          </div>

          {/* Manual Approval Mode section */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-2 text-lg flex items-center gap-2">
              <div className="bg-amber-500/20 text-amber-500 rounded-full p-1.5">
                <MessageCircle className="h-4 w-4" />
              </div>
              Manual Approval Mode
            </h4>
            <p className="text-muted-foreground">
              In manual approval mode, your AI agent will generate potential
              replies but hold them for your review. You can approve them as-is,
              edit them, or reject them completely. This gives you complete
              control over what gets posted to your Twitter account.
            </p>
            <div className="mt-3 bg-muted p-3 rounded-md">
              <p className="text-sm font-medium">When to Use:</p>
              <ul className="list-disc pl-5 text-sm text-muted-foreground mt-1 space-y-1">
                <li>When first setting up your Twitter agent</li>
                <li>For sensitive accounts or topics</li>
                <li>When quality control is critical</li>
                <li>To learn what responses your agent generates</li>
              </ul>
            </div>
          </div>

          {/* Moderation & Spam Protection section */}
          <div className="p-4 border rounded-md">
            <h4 className="font-medium mb-2 text-lg flex items-center gap-2">
              <div className="bg-red-500/20 text-red-500 rounded-full p-1.5">
                <ShieldAlert className="h-4 w-4" />
              </div>
              Moderation & Spam Protection
            </h4>
            <p className="text-muted-foreground">
              Your Twitter reply system includes moderation rules and spam
              detection to help you manage interactions:
            </p>
            <div className="mt-3 bg-muted p-3 rounded-md">
              <p className="text-sm font-medium">Key Features:</p>
              <ul className="list-disc pl-5 text-sm text-muted-foreground mt-1 space-y-1">
                <li>
                  <span className="font-medium">Skipped Replies:</span> Messages
                  that don't meet your moderation criteria
                </li>
                <li>
                  <span className="font-medium">Spam Detection:</span> Automatic
                  identification of potentially harmful or spam messages
                </li>
                <li>
                  <span className="font-medium">Configurable Rules:</span> Set
                  criteria for which mentions to reply to
                </li>
              </ul>
            </div>
          </div>

          {/* Pro Tips section */}
          <div>
            <h4 className="font-medium mb-2">Pro Tips</h4>
            <ul className="list-disc pl-5 text-muted-foreground space-y-2">
              <li>
                Start with manual approval to understand your agent's style and
                tone, then switch to automated mode once you're confident in its
                responses.
              </li>
              <li>
                Use the "Process Specific Tweet" feature to handle high-priority
                mentions immediately.
              </li>
              <li>
                Even in automated mode, you can still view all the replies your
                agent has posted in the "Replied" tab.
              </li>
              <li>
                Configure reply rules to filter out low-quality engagement and
                focus on meaningful conversations.
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HelpModal;
